<template>
  <section class="project-issue-page">
    <div
      class="container"
      v-if="issue"
    >
      <div class="d-flex flex-column border-bottom mb-3 pb-3">
        <div
          class="d-flex flex-md-row flex-column align-items-md-center justify-content-md-between"
        >
          <h4 class="text-break mr-2">
            <span class="text-secondary mr-1">#{{ issue.localId }}</span>
            {{ issue.title }}
          </h4>
          <div
            class="d-flex flex-shrink-1 mt-2 mt-md-0"
            v-if="hasEditAccess"
          >
            <div class="col-6 col-md pr-1 pl-0">
              <a
                :href="
                  '/project/' +
                  issue.userAlias +
                  '/' +
                  issue.projectAlias +
                  '/issue/' +
                  issue.localId +
                  '/edit'
                "
                class="btn btn-block btn-dark btn-sm"
              >
                {{ $t('message.edit') }}
              </a>
            </div>
            <div
              class="col-6 col-md pr-1 pl-0"
              v-if="getActionAvailabilityFlag(ACTION_ENUM.CREATE_ISSUE)"
            >
              <a
                :href="'/project/' + issue.userAlias + '/' + issue.projectAlias + '/issue/create'"
                class="btn btn-block btn-sm btn-success"
              >
                {{ $t('message.create') }}
              </a>
            </div>
          </div>
        </div>
        <div class="d-flex align-items-center mt-3 mt-md-0">
          <status-label
            class="mr-3"
            :status="issue.status"
          />
          <DiscussionCounter v-if="getActionAvailabilityFlag(ACTION_ENUM.READ_ISSUE_NOTE)" />
          <div class="text-muted d-flex align-items-center">
            <a :href="'/user/' + issue.updatedBy.username">
              <img
                :src="issue.updatedBy.avatar"
                class="rounded-circle mr-1"
                height="20px"
                width="20px"
              />
            </a>

            <a
              :href="'/user/' + issue.updatedBy.username"
              class="font-weight-bolder text-muted mr-1"
            >
              {{ issue.updatedBy.fullName }}
            </a>
            {{ $tc('issue_create', 2, { at: issue.createdAt }) }}
          </div>
        </div>
      </div>
      <div class="row">
        <div class="col-12 col-md-9 d-flex flex-column order-2 order-md-1">
          <div class="w-100 border rounded mb-3">
            <div
              class="markdown-body px-4 py-3 break-word"
              v-html="issue.descriptionHtml"
            ></div>
          </div>
          <div
            class="w-100"
            v-if="getActionAvailabilityFlag(ACTION_ENUM.READ_ISSUE_NOTE)"
          >
            <project-issue-discussion-message
              v-for="discussion in startDiscussions"
              :key="discussion.id"
              :discussion="discussion"
            />
            <empty-listing
              v-if="
                !!startDiscussions &&
                !startDiscussions.length &&
                !!endDiscussions &&
                !endDiscussions.length
              "
              :title="$t('discussion_no')"
            />
            <div
              class="load-more-discussion-container text-center mb-3"
              v-if="discussionsPag && discussionsPag.number > 1"
            >
              <button
                @click.prevent="loadMore"
                class="btn btn-sm btn-primary"
              >
                {{
                  $tc('message.show_more', 1, [
                    this.discussionsPag.size,
                    this.discussionsPag.totalElements -
                      this.startDiscussions.length -
                      this.endDiscussions.length
                  ])
                }}
              </button>
            </div>

            <project-issue-discussion-message
              v-for="discussion in endDiscussions"
              :key="discussion.id"
              :discussion="discussion"
            />

            <div
              class="d-flex flex-column flex-grow-1 mb-3 mt-3 border-top pt-3"
              v-if="getActionAvailabilityFlag(ACTION_ENUM.CREATE_ISSUE_NOTE)"
            >
              <div class="d-flex justify-content-center">
                <button
                  type="button"
                  v-show="isShown"
                  class="btn btn-primary btn-sm"
                  data-editor="commentIssueEditor"
                  @click="toggleHandler"
                >
                  {{ $tc('message.comment', 1) }}
                </button>

                <div
                  v-if="isShowingEditor"
                  class="text-break w-100"
                >
                  <div class="card">
                    <div class="d-flex card-body bg-light rounded">
                      <gf-custom-text-editor
                        class="w-100"
                        editor-variant="editor"
                        open-button-data-name="commentIssueEditor"
                        :placeholder="$t('writeCommentTitle')"
                        :has-error="formErrors && formErrors.note"
                        :error-text="formErrors?.note"
                        :has-close-button="true"
                        :submit-button-text="$tc('message.comment', 1)"
                        @close="hideHandler"
                        @hide="hideHandler"
                        @submit="submit"
                      ></gf-custom-text-editor>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="col-12 col-md-3 order-1 order-md-2 mb-5">
          <div
            class="form-group"
            v-if="hasStatusEditAccess"
          >
            <label
              class="font-weight-bolder"
              for="status"
            >
              {{ $tc('message.status', 1) }}
            </label>
            <custom-select
              :placeholder="$tc('message.status', 2)"
              :options="statusRef"
              v-model="selectedStatus"
              @input="changeIssueStatus"
            >
              <custom-selector-dot-option
                slot-scope="{ option }"
                :hex-color="option.hexColor"
                :option="option"
              />
            </custom-select>
          </div>
          <div class="d-flex flex-column mb-3 pt-3 border-top">
            <span class="font-weight-bolder mb-2">{{ $tc('message.assigned', 1) }}</span>
            <user-label-item
              v-for="user in issue.assignedUsers"
              :user="user"
              :key="user.id"
              :has-user-link="true"
              :open-in-new-tab="false"
            />
            <empty-listing
              v-if="!!issue.assignedUsers && !issue.assignedUsers.length"
              :title="$tc('message.assigned', 2)"
              type="small"
            />
          </div>
          <div class="d-flex flex-column pt-3 border-top">
            <span class="font-weight-bolder mb-2">{{ $tc('message.label', 1) }}</span>
            <div class="d-flex flex-wrap">
              <repo-label-list
                :labels="issue.labels"
                :deletable="false"
              />
            </div>
            <empty-listing
              v-if="!!issue.labels && !issue.labels.length"
              :title="$tc('message.label', 2)"
              type="small"
            />
          </div>
          <div
            class="mt-4"
            v-if="getActionAvailabilityFlag(ACTION_ENUM.DELETE_ISSUE)"
          >
            <button
              class="d-flex align-items-center btn btn-sm justify-content-center w-100 btn-outline-danger"
              data-target="deleteIssueModal"
            >
              <i class="gf-icon gf-icon-trash"></i>
              {{ $t('issueDelete') }}
            </button>

            <gf-custom-modal-window
              ref="gfCustomModalWindowDeleteIssue"
              gf-modal-data-name="deleteIssueModal"
              :gf-modal-header="$t('issueDeleteConfirmation')"
              gf-modal-wrapper-width="500px"
            >
              <template #body>
                <div class="d-flex justify-content-end">
                  <button
                    type="button"
                    class="btn btn-secondary btn-sm m-1"
                    @click="closeDeleteForm"
                  >
                    {{ $t('cancel') }}
                  </button>
                  <button
                    type="button"
                    class="btn btn-primary btn-sm btn-danger m-1"
                    @click="deleteIssue"
                  >
                    {{ $t('delete') }}
                  </button>
                </div>
              </template>
            </gf-custom-modal-window>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import IssueS from '@shared/services/issue.service';
import RepoLabelList from '@shared/shared-components/shared/repo-label-list';
import UserLabelItem from '@shared/shared-components/shared/user-label-item';
import CustomSelect from '@shared/shared-components/shared/custom-select';
import RefS from '@shared/services/reference.service';
import CustomSelectorDotOption from '@shared/shared-components/shared/custom-selector-dot-option';
import ProjectIssueDiscussionMessage from '../components/project-issue-discussion-message';
import EmptyListing from '@shared/shared-components/empty-listing';
import Vue from 'vue';
import { FORM_NAME_ENUMS } from '@shared/variables/form-name.enums';
import issueMessages from '@shared/modules/project-issue/issueMessages';
import StatusLabel from '@shared/shared-components/status-label';
import DiscussionCounter from '@shared/modules/project-issue/components/project-issue-discussion-counter';
import gfCustomModalWindow from '@shared/shared-components/shared/GfCustomModalWindow.vue';
import { smallNotification } from '@shared/services/gf.notification.service';
import GfCustomTextEditor from '@/shared-modules/shared-components/shared/GfCustomTextEditor.vue';
import ACTION_ENUM from '@shared/modules/custom-roles/custom-roles.constants';

export default {
  components: {
    StatusLabel,
    ProjectIssueDiscussionMessage,
    CustomSelectorDotOption,
    CustomSelect,
    UserLabelItem,
    RepoLabelList,
    EmptyListing,
    DiscussionCounter,
    gfCustomModalWindow,
    GfCustomTextEditor,
    GfCustomTextEditor
  },
  mounted() {
    this.checkCommentAccess();

    this.hasEditAccess =
      document.getElementById('hasEditAccess') &&
      document.getElementById('hasEditAccess').value === 'true';

    this.hasStatusEditAccess =
      document.getElementById('hasStatusEditAccess') &&
      document.getElementById('hasStatusEditAccess').value === 'true';

    // если может редактировать полностью, то и статус может
    this.hasStatusEditAccess = this.hasEditAccess || this.hasStatusEditAccess;

    IssueS.getIssue(
      this.$route.params.alias,
      this.$route.params.projectAlias,
      this.$route.params.issueId
    ).then((res) => {
      this.selectedStatus = res.status;
    });

    if (this.getActionAvailabilityFlag(ACTION_ENUM.READ_ISSUE_NOTE)) {
      IssueS.getDiscussions(
        this.$route.params.alias,
        this.$route.params.projectAlias,
        this.$route.params.issueId,
        this.$route.query
      ).then((res) => {
        this.startDiscussions = res?.items;
        if (res.page.totalPages > 1) {
          IssueS.getDiscussions(
            this.$route.params.alias,
            this.$route.params.projectAlias,
            this.$route.params.issueId,
            { ...this.$route.query, page: res.page.totalPages }
          ).then((endRes) => {
            this.endDiscussions = endRes?.items;
            this.setPagination(endRes);
          });
        }
      });
    }

    RefS.getRefIssueStatuses(this.$route.params.alias, this.$route.params.projectAlias);
  },
  data() {
    return {
      selectedStatus: null,
      commentMessage: null,
      startDiscussions: [],
      endDiscussions: [],
      discussionsPag: {},
      hasEditAccess: false,
      hasStatusEditAccess: false,
      form: {},
      isShown: false,
      counterValue: 0,
      discussionStatusValue: 0,
      isShowingEditor: false
    };
  },
  methods: {
    getActionAvailabilityFlag(action) {
      return this.$store.getters.getActionAvailabilityFlag(action);
    },
    setPagination(pager) {
      this.discussionsPag = pager.page;
    },
    loadMore() {
      if (this.discussionsPag.number <= 1) return;

      IssueS.getDiscussions(this.issue.userAlias, this.issue.projectAlias, this.issue.localId, {
        ...this.$route.query,
        page: this.discussionsPag.number
      }).then((res) => {
        Vue.set(this, 'endDiscussions', [...res.items, ...this.endDiscussions]);
        this.setPagination(res);
      });
    },
    getEditorData() {
      this.commentMessage = this.$refs.toastuiEditor.invoke('getMarkdown');
    },
    changeIssueStatus(data) {
      IssueS.issueChangeStatus(
        this.issue.userAlias,
        this.issue.projectAlias,
        this.issue.localId,
        data
      ).then(() => {
        smallNotification('success', { title: 'Статус обновлен!' });
      });
    },
    async submit(data) {
      let note = await data;
      if (note) {
        IssueS.postComment(this.issue.userAlias, this.issue.projectAlias, this.issue.localId, {
          note: note
        }).then((res) => {
          this.endDiscussions.push(res);
          this.commentMessage = '';
          if (this.formErrors && this.formErrors.note) delete this.formErrors.note;
          this.$store.commit('addDiscussionsCounter', this.getCounter + 1);
          this.hideHandler();
        });
      }
    },
    deleteIssue() {
      IssueS.deleteIssue(this.issue.userAlias, this.issue.projectAlias, this.issue.localId).then(
        () =>
          (window.location = `/project/${this.issue.userAlias}/${this.issue.projectAlias}/issue`)
      );
      this.closeDeleteForm();
    },
    checkCommentAccess() {
      const token = document.querySelector('head meta[name="uuid"]');
      if (!token) return;
      this.isShown = true;
    },
    closeDeleteForm() {
      this.$refs.gfCustomModalWindowDeleteIssue.closeModalWindow();
    },
    toggleHandler() {
      this.isShowingEditor = true;
      this.isShown = false;
    },
    hideHandler() {
      this.isShowingEditor = false;
      this.isShown = true;
    }
  },
  computed: {
    ACTION_ENUM() {
      return ACTION_ENUM;
    },
    issue() {
      return this.$store.getters.getIssue;
    },
    statusRef() {
      return this.$store.getters.getRefIssueStatus;
    },
    discussions() {
      return this.$store.getters.getIssueDiscussions;
    },
    formErrors() {
      return this.$store.getters.getFormErrors(FORM_NAME_ENUMS.ISSUE_DISCUSSION_FORM);
    },
    getCounter() {
      return this.$store.getters.getDiscussionsCounter;
    }
  },
  i18n: {
    messages: issueMessages
  }
};
</script>

<style lang="scss" scoped>
.markdown-body::v-deep p:last-child {
  margin-bottom: 0;
}
</style>
